/**
 * Turn 1: live adaptive sample question. Emits AdaptiveQuestionResult on Continue,
 * after Pam's read has been seen.
 */
import React, { useMemo, useRef, useState } from 'react';
import type { AdaptiveQuestionResult } from '../types';
import {
  buildAdaptiveResult,
  pickTurn1Question,
  type AdaptiveTurnQuestion,
} from '../adaptiveEngine';
import { AdaptiveQuestionBody } from './DiagnosticQuestion';

export interface AdaptiveQuestionTurnProps {
  question?: AdaptiveTurnQuestion;
  /** Lane A `commitTurn` when present; otherwise parent persists via onComplete. */
  commitTurn?: (result: AdaptiveQuestionResult) => void | Promise<void>;
  onComplete: (result: AdaptiveQuestionResult) => void;
}

export const AdaptiveQuestionTurn: React.FC<AdaptiveQuestionTurnProps> = ({
  question: questionProp,
  commitTurn,
  onComplete,
}) => {
  const question = useMemo(() => questionProp ?? pickTurn1Question(), [questionProp]);
  const [choiceId, setChoiceId] = useState<string | null>(null);
  const done = useRef(false);

  const finish = async () => {
    if (done.current || choiceId === null) return;
    done.current = true;
    const result = buildAdaptiveResult({ turnId: 1, kind: 'adaptive_question', question, choiceId });
    if (commitTurn) await commitTurn(result);
    onComplete(result);
  };

  return (
    <section data-testid="adaptive-question-turn" data-turn="1">
      <AdaptiveQuestionBody
        question={question}
        choiceId={choiceId}
        onSubmit={setChoiceId}
        onContinue={() => void finish()}
      />
    </section>
  );
};
